<template>
  <v-container
    fluid
    class="create-custom">
    <!-- META-DATA -->
    <vue-headful
      :title="'Custom Patch Creation Tool' + this.$store.state.app.meta.titleSuffix"
      :description="this.$store.state.app.meta.description" />

    <v-scroll-y-transition
      group
      hide-on-leave
      class="stepper">
      <div
        v-if="stepper===0"
        key="upload"
        class="stepper__item">
        <custom-upload
          @set-source-image="onUpload($event)" />
      </div>

      <div
        v-if="stepper===1"
        key="format"
        class="stepper__item">
        <custom-format
          ref="customFormat"
          :source-image="sourceImageURL"
          @canvas-output="onCanvasOutput($event)"
          @landscape-next="nextStep" />
      </div>

      <div
        v-if="stepper===2"
        key="preview"
        class="stepper__item">
        <v-container class="custom-preview">
          <div class="custom-preview__image-column">
            <img
              :src="outputImageURL"
              class="custom-patch-image">
          </div>
          <div class="custom-patch-info">
            <h2 class="custom-patch-info__header">
              all set
            </h2>
            <p class="custom-patch-info__body">
              If you like what you see, then your patch is ready for printing; all that's left is to add it to your cart. Or you can go back and make some edits before continuing.
            </p>
            <v-divider />
            <div class="custom-patch-info__add-to-cart-wrapper">
              <v-btn
                large
                color="primary"
                class="custom-patch-info__add-to-cart"
                @click="addToCart">
                add to cart
              </v-btn>
            </div>
            <v-divider />
          </div>
        </v-container>
      </div>
    </v-scroll-y-transition>


    <nav class="nav-controls">
      <v-scroll-x-reverse-transition>
        <v-btn
          v-if="stepper > 0"
          flat
          outline
          small
          :disabled="stepper===0"
          class="nav-controls__previous"
          @click="prevStep">
          back
        </v-btn>
      </v-scroll-x-reverse-transition>

      <div class="nav-controls__stepper">
        <div
          v-for="step in steps"
          :key="step"
          class="nav-controls__stepper-step-wrapper">
          <div
            class="nav-controls__stepper-step"
            :class="{
              'nav-controls__stepper-step--current': stepper===steps.indexOf(step),
              'nav-controls__stepper-step--complete': stepper > steps.indexOf(step)
            }">
            <v-icon
              class="nav-controls__stepper-icon">
              {{ step }}
            </v-icon>
          </div>
        </div>
      </div>

      <v-scroll-x-transition>
        <v-btn
          v-if="stepper > 0"
          flat
          outline
          small
          color="primary"
          :disabled="stepper>=2"
          class="nav-controls__next"
          @click="nextStep">
          next
        </v-btn>
      </v-scroll-x-transition>
    </nav>
  </v-container>
</template>

<script>
import CustomUpload from '../../components/patchCreator/CustomUpload.vue';
import CustomFormat from '../../components/patchCreator/CustomFormat.vue';

export default {
  name: 'CreateCustom',
  components: {
    CustomUpload,
    CustomFormat,
  },
  data() {
    return {
      stepper: 0,
      steps: [
        'add_a_photo',
        'tune',
        'shopping_cart',
      ],
      sourceImageURL: '',
      outputImageURL: '',
    };
  },
  computed: {
    customPatchPrice() {
      return this.$store.state.cart.customPatchPrice;
    },
  },
  methods: {
    prevStep() {
      this.stepper--;
    },
    nextStep() {
      if (this.stepper === 1) {
        this.createCanvas();
      }
      this.stepper++;
    },
    onUpload(event) {
      this.sourceImageURL = event;
      this.stepper++;
    },
    onCanvasOutput(event) {
      this.outputImageURL = event;
    },
    createCanvas() {
      this.$refs.customFormat.createCanvas();
    },
    addToCart() {
      this.$store.dispatch('cart/addCustomPatchToCart', this.outputImageURL);
      this.$store.dispatch('app/openMenu', 'cartDrawer');
      this.$router.push({ path: '/custom' });
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../../stylus/base')

.create-custom
  position: relative;
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: space-between
  padding: 0
  height: 99%
  max-width: none

.stepper
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: center
  width: 100%
  height: 100%
  &__item
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    height: 100%

.nav-controls
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  width: 100%
  max-width: 28rem
  transition: $base-transition
  @media screen and (max-width: 48rem)
    max-width: 100%
  &__stepper
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    width: 100%
    &::before
      content: ''
      position: absolute
      z-index: 0
      height: 1px
      width: 100%
      background-color: $accent
    &-step-wrapper
      z-index: 1
      padding: 0 $base-padding
      background-color: $white
      border-radius: $base-border-radius
    &-step
      display: flex
      flex-flow: column nowrap
      align-items: center
      justify-content: center
      // margin-right: $base-padding
      // margin-left: $base-padding
      padding: $base-padding*.5
      color: $accent
      border-radius: $base-border-radius*2
      transition: $base-transition
      &--current
        color: $secondary
        background-color: $primary
      &--complete
        color: $white
        background-color: $accent
    &-icon
      color: inherit
  &__previous,
  &__next
    @media screen and (max-width: 28rem)
      min-width: 0
      padding: 0 $base-padding*.5


// CUSTOM PREVIEW STEP
.custom-preview
  display: flex
  flex-flow: row wrap
  align-content: flex-start
  height: 100%
  &__image-column
    position: relative
    display: flex
    flex-flow: column nowrap
    flex: 1 1 0
    align-items: center
    justify-content: flex-start

    padding-right: $base-padding*.5
    @media screen and (max-width: 48rem)
      flex-basis: 100%
      padding-right: 0

.custom-patch-image
  max-width: 100%

.custom-patch-info
  flex: 1 1 0
  padding-left: $base-padding*.5
  @media screen and (max-width: 48rem)
    flex-basis: 100%
    padding-left: 0
  &__header
    font-size: 1.75rem
  &__add-to-cart-wrapper
    display: flex
    align-items: center
    justify-content: space-between
    padding-top: $base-padding
    padding-bottom: $base-padding
  &__add-to-cart
    display: flex
    flex: 1 1 100%
    margin: 0


</style>
